import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, 
  RefreshCcw, 
  ChevronRight, 
  ShieldCheck, 
  Zap, 
  Terminal, 
  Database, 
  Globe,
  Settings,
  AlertCircle,
  CheckCircle2,
  Cpu,
  Monitor,
  Clock
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> = 
  ({ label, value, icon, color, subText }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

type TabType = 'sessions' | 'messages' | 'config';

const FixMonitoringScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('sessions');
  const [sessions, setSessions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFixData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/fix/sessions');
      setSessions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching FIX data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFixData();
    const interval = setInterval(fetchFixData, 30000);
    return () => clearInterval(interval);
  }, [fetchFixData]);

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Monitor className="w-8 h-8 text-blue-500" />
                FIX Gateway Monitor
              </h1>
              <p className="text-slate-500 font-medium mt-1">Real-time surveillance of institutional connectivity and protocol health</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                {['sessions', 'messages', 'config'].map((tab) => (
                  <button 
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize", 
                      activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <button 
                onClick={fetchFixData}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Active Sessions" value={String(sessions.filter(s => s.status === 'LOGGED_ON').length)} icon={<Globe className="w-5 h-5" />} color="#10b981" subText="Connected to Venues" />
            <StatCard label="Idle Sessions" value={String(sessions.filter(s => s.status !== 'LOGGED_ON').length)} icon={<Clock className="w-5 h-5" />} color="#f59e0b" subText="Maintenance required" />
            <StatCard label="Protocol Health" value="100%" icon={<ShieldCheck className="w-5 h-5" />} color="#3b82f6" subText="No latency gaps" />
            <StatCard label="Gateway Throughput" value="1.2k req/s" icon={<Activity className="w-5 h-5" />} color="#8b5cf6" subText="Processing peak" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <GlassCard className="lg:col-span-2 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Terminal className="w-5 h-5 text-blue-400" />
                Connectivity Grid
              </h3>
              <button className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">Reload All Sessions</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="pb-3 px-2">Session Name</th>
                    <th className="pb-3 px-2">Venue</th>
                    <th className="pb-3 px-2">Target CompID</th>
                    <th className="pb-3 px-2 text-right">Sequence</th>
                    <th className="pb-3 px-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {sessions.map((s, i) => (
                    <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                      <td className="py-4 px-2 font-bold text-white">{s.sessionName}</td>
                      <td className="py-4 px-2 font-medium text-slate-400">{s.venue}</td>
                      <td className="py-4 px-2 font-mono text-slate-500">{s.targetCompId}</td>
                      <td className="py-4 px-2 text-right font-mono text-slate-400">
                        <span className="text-emerald-400">{s.outSequence}</span> / <span className="text-blue-400">{s.inSequence}</span>
                      </td>
                      <td className="py-4 px-2 text-right">
                        <span className={cn(
                          "px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                          s.status === 'LOGGED_ON' ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"
                        )}>
                          {s.status === 'LOGGED_ON' ? 'Active' : 'Offline'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-500 italic">No FIX sessions configured in gateway</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>

          <div className="space-y-6">
            <GlassCard className="p-6">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-blue-400" />
                Gateway Performance
              </h3>
              <div className="space-y-6">
                {[
                  { label: 'Message Latency', value: '4ms', pct: 15, color: '#10b981' },
                  { label: 'CPU Utilization', value: '24%', pct: 24, color: '#3b82f6' },
                  { label: 'Memory Pressure', value: '1.2GB', pct: 45, color: '#f59e0b' },
                  { label: 'TCP Retransmits', value: '0.01%', pct: 5, color: '#ef4444' }
                ].map(stat => (
                  <div key={stat.label} className="space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-xs font-bold text-slate-500 uppercase">{stat.label}</span>
                      <span className="text-sm font-bold text-white">{stat.value}</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full transition-all duration-1000" style={{ width: `${stat.pct}%`, backgroundColor: stat.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>

            <GlassCard className="p-6 bg-gradient-to-br from-blue-600/10 to-violet-600/10 border-blue-500/20 group">
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
                  <Database className="w-6 h-6" />
                </div>
                <button className="text-xs font-bold text-blue-400 hover:text-white transition-colors">Download Logs</button>
              </div>
              <h4 className="font-bold text-white mb-1">Persistent FIX Store</h4>
              <p className="text-xs text-slate-400 leading-relaxed mb-4">The gateway is using high-performance disk storage for message persistence and sequence recovery.</p>
              <div className="flex items-center gap-2 text-[10px] font-bold text-emerald-400 uppercase">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Sync Healthy
              </div>
            </GlassCard>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default FixMonitoringScreen;
